import { useState, useEffect, memo, useMemo, useCallback, useRef } from 'react';
import { EDSTextField } from '@gehc-eds-react/eds-text-field';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { addErrorNotification, addSuccessNotification } from '../../redux/slices/notificationSlice';
import {
    clearSources,
    fetchSources,
    createSource,
    deleteSource,
    testSourceConnection,
    selectSourcesPagination,
    selectIsCreatingSource,
    selectIsUpdatingSource,
    selectIsDeletingSource,
    selectIsTestingConnection,
} from '../../redux/slices/s3SourcesSlice';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';
import { EditSourceModal, DeleteSourceModal } from './SourceModel';
import TestConnectionModal from './TestConnectionModel';
import SourceListTable from './SourceListTable';
import logger from '../../utils/logger';

const ACCOUNTS_PAGE_SIZE = 10;

const PrefilledField = ({ name, label, value }) => (
    <div className="u-mt onboarding-prefilled-field">
        <label htmlFor={name} style={{ display: 'block', marginBottom: '6px', color: 'var(--eds-text-icon-default)' }}>
            {label}
        </label>
        <input
            id={name}
            name={name}
            type="text"
            value={value || ''}
            readOnly
            style={{ width: '100%', height: '36px', padding: '0 12px', border: '1px solid transparent', borderRadius: '4px', backgroundColor: '#f5f6f7', color: '#8a8f98' }}
        />
    </div>
);

const CreateS3Upload = ({ onClose, selectedContract }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // ── Register Source form state ──
    const [accountName, setAccountName] = useState('');
    const [bucketName, setBucketName] = useState('');
    const [prefix, setPrefix] = useState('');
    const [existingDatasetName, setExistingDatasetName] = useState('');

    const [accountNameError, setAccountNameError] = useState('');
    const [bucketNameError, setBucketNameError] = useState('');
    const [prefixError, setPrefixError] = useState('');
    const [isBucketInfoVisible, setIsBucketInfoVisible] = useState(false);

    // ── Source list state from Redux ──
    // Only pagination is needed here directly (to compute the next/previous
    // page number for fetching). Accounts, load status, and error are read
    // by SourceListTable itself, straight from the slice.
    const accountsPagination = useAppSelector(selectSourcesPagination);
    const isCreatingSource = useAppSelector(selectIsCreatingSource);
    const isUpdatingSource = useAppSelector(selectIsUpdatingSource);
    const isDeletingSource = useAppSelector(selectIsDeletingSource);
    const isTestConnectionRunning = useAppSelector(selectIsTestingConnection);

    // ── Delete Source modal state ──
    const [sourceToDelete, setSourceToDelete] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // ── Test Connection modal state (UI-only, not persisted) ──
    const [testConnectionModal, setTestConnectionModal] = useState({ isOpen: false, account: null, result: null, error: null });

    const [prefilledData, setPrefilledData] = useState({
        dataPartner: '', businessUnit: '', project: '', contract: '', contractId: ''
    });

    useEffect(() => {
        if (selectedContract) {
            setPrefilledData({
                dataPartner: selectedContract.DataPartnerDisplayName || selectedContract.DataPartnerName || '',
                businessUnit: selectedContract.BusinessUnit || '',
                project: selectedContract.Project || '',
                contract: selectedContract.ContractDisplayName || selectedContract.Name || '',
                contractId: selectedContract.ContractID || ''
            });
        }
    }, [selectedContract]);

    const selectedContractId = useMemo(
        () => selectedContract?.ContractID || selectedContract?.contract_id || '',
        [selectedContract]
    );

    // ── Load / paginate source accounts ──

    const loadAccountsPage = useCallback((page) => {
        if (!selectedContractId) return;
        dispatch(fetchSources({ contractId: selectedContractId, page, pageSize: ACCOUNTS_PAGE_SIZE }));
    }, [dispatch, selectedContractId]);

    useEffect(() => {
        dispatch(clearSources());
        loadAccountsPage(1);
    }, [dispatch, loadAccountsPage]);

    // ── Register Source (onboarding) submit ──

    const validateAccountNumber = (account) => {
        if (!account || !account.trim()) return 'Account number is required.';
        if (!/^\d{12}$/.test(account.trim())) return 'Account number must be 12 digits.';
        return '';
    };

    const validateBucketNameLive = (value) => {
        if (!value) return '';
        if (!/^[a-z0-9.-]+$/.test(value)) {
            return 'Only lowercase letters, numbers, dots, and hyphens are allowed.';
        }
        if (value.length < 3) {
            return `${3 - value.length} more character(s) required.`;
        }
        if (value.length > 63) {
            return 'Bucket name must be at most 63 characters.';
        }
        return '';
    };

    const resetFormState = () => {
        setAccountName('');
        setBucketName('');
        setPrefix('');
        setExistingDatasetName('');
        setAccountNameError('');
        setBucketNameError('');
        setPrefixError('');
    };

    const getValidationMessages = (error) => {
        const details = Array.isArray(error?.detail) ? error.detail : [];
        if (details.length > 0) {
            return details.map((item) => {
                const location = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : '';
                const message = item?.msg || '';
                const labelMap = { source_bucket: 'Bucket Name', display_name: 'Display Name', account_id: 'Account Number', replicate_prefix: 'Prefix' };
                const fieldLabel = labelMap[location] || '';
                if (!message) return '';
                return fieldLabel ? `${fieldLabel}: ${message}` : message;
            }).filter(Boolean);
        }
        return error?.message ? [error.message] : [];
    };

    const applyBackendFieldErrors = (error) => {
        const details = Array.isArray(error?.detail) ? error.detail : [];
        let hasFieldError = false;

        if (details.length > 0) {
            details.forEach((item) => {
                const location = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : '';
                const message = item?.msg || '';
                if (!message) return;
                if (location === 'source_bucket') { setBucketNameError(message); hasFieldError = true; }
                if (location === 'account_id') { setAccountNameError(message); hasFieldError = true; }
                if (location === 'replicate_prefix') { setPrefixError(message); hasFieldError = true; }
            });
            return hasFieldError;
        }

        const backendMessage = (error?.message || '').trim();
        const normalizedMessage = backendMessage.toLowerCase();
        if (normalizedMessage.includes('bucket')) { setBucketNameError(backendMessage); return true; }
        if (normalizedMessage.includes('account')) { setAccountNameError(backendMessage); return true; }
        if (normalizedMessage.includes('prefix')) { setPrefixError(backendMessage); return true; }
        return false;
    };

    const handleSubmit = async () => {
        const accountErr = validateAccountNumber(accountName);
       
        const bucketErr = !bucketName.trim()
            ? 'Bucket name is required.'
            : validateBucketNameLive(bucketName.trim());
        setAccountNameError(accountErr);
        setBucketNameError(bucketErr);
        if (accountErr || bucketErr) return;

        let finalPrefix = prefix.trim() || '/';
        if (finalPrefix !== '/' && !finalPrefix.endsWith('/')) finalPrefix += '/';

        const payload = {
            account_id: accountName.trim(),
            replicate_prefix: finalPrefix,
            source_bucket: bucketName.trim(),
            contract_id: prefilledData.contractId,
            display_name: existingDatasetName.trim()
        };

        try {
            await dispatch(createSource({ payload, contractId: prefilledData.contractId })).unwrap();
            dispatch(addSuccessNotification({
                message: `S3 source onboarding for "${bucketName.trim()}" initiated successfully.`
            }));
            resetFormState();
        } catch (error) {
            logger.error('S3 onboarding error:', error);
            applyBackendFieldErrors(error);
            const validationMessages = getValidationMessages(error);
            dispatch(addErrorNotification({
                message: validationMessages.length > 0 ? validationMessages.join(' | ') : 'Failed to onboard S3 source. Please try again.'
            }));
        }
    };

    const handleCancel = () => onClose();

    // ── Delete Source ──

    const handleDeleteAccount = (account) => {
        setSourceToDelete(account);
        setIsDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        if (isDeletingSource) return;
        setIsDeleteConfirmOpen(false);
        setSourceToDelete(null);
    };

    const confirmDeleteAccount = async () => {
        const sourceId = sourceToDelete?.source_id || sourceToDelete?.sourceId || sourceToDelete?.id;
        if (!sourceId) {
            dispatch(addErrorNotification({ message: 'Deboard failed: source id is missing for this account.' }));
            closeDeleteConfirm();
            return;
        }

        try {
            await dispatch(deleteSource({
                sourceId,
                contractId: selectedContractId || prefilledData.contractId,
                page: accountsPagination.page,
                accountId: sourceToDelete?.account_id,
            })).unwrap();
            closeDeleteConfirm();
        } catch {
            // Error notification already dispatched by the thunk
        }
    };

    // ── Test Connection ──

    const getAccountSourceId = (account) => account?.source_id || account?.sourceId || account?.id;

    const runTestConnection = async (account) => {
        const sourceId = getAccountSourceId(account);
        if (!sourceId) {
            dispatch(addErrorNotification({ message: 'Test connection failed: source id is missing for this account.' }));
            return;
        }

        try {
            const { result } = await dispatch(testSourceConnection({
                sourceId,
                account,
                contractId: selectedContractId || prefilledData.contractId,
                page: accountsPagination.page,
            })).unwrap();
            setTestConnectionModal({ isOpen: true, account, result, error: null });
        } catch (rejected) {
            setTestConnectionModal({
                isOpen: true,
                account: rejected?.account ?? account,
                result: null,
                error: rejected?.error || 'Failed to test connection. Please try again.',
            });
        }
    };

    const handleTestConnection = (account) => runTestConnection(account);

    const handleRunTestConnectionAgain = () => {
        if (testConnectionModal.account) runTestConnection(testConnectionModal.account);
    };

    const closeTestConnectionModal = () => {
        if (isTestConnectionRunning) return;
        setTestConnectionModal({ isOpen: false, account: null, result: null, error: null });
    };

    // ── Navigation ──

    const handleOpenSourceDetails = (account) => {
        const sourceId = account?.source_id || account?.sourceId || account?.id || account?.account_id || 'source';
        navigate(`/s3-upload/source/${encodeURIComponent(sourceId)}`, { state: { source: account, selectedContract } });
    };

    return (
        <><div className="data-upload-container" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <SpinnerComponent isVisible={isCreatingSource} overlay={true} message="Registering Source..." />
            <SpinnerComponent isVisible={isTestConnectionRunning} overlay={true} message="Testing connection..." />

            <div className="upload-data-header fill--1">
                <h1 className="u-mt0 u-mb0">S3 to S3 Upload</h1>
            </div>

            <div className="flex user-groups-container" style={{ flex: '1 1 auto', minHeight: 0 }}>
                {/* ── Left panel: Register Source form ── */}
                <div className="user-details-section u--1/4 fill--1" style={{ paddingBottom: '24px' }}>
                    <div><h4 className="u-mt0 u-mb--">Register Source</h4></div>

                    <div className="u-mt">
                        <EDSTextField
                            name="account-number"
                            isInvalid={Boolean(accountNameError)}
                            label={{ width: '', position: 'top', text: 'Account Number' }}
                            placeholder="Enter 12-digit account number"
                            isRequired={true}
                            validationError={accountNameError}
                            width="100%"
                            type="text"
                            value={accountName}
                            maxLength={12}
                            onEDSTextFieldChange={(e) => {
                                const inputValue = e.target.value;
                                const numericValue = inputValue.replace(/[^\d]/g, '');
                                const limitedValue = numericValue.slice(0, 12);
                                setAccountName(limitedValue);
                                if (inputValue !== numericValue) {
                                    setAccountNameError('Account number must contain only digits.');
                                } else if (limitedValue.length === 12 && inputValue.length > 12) {
                                    setAccountNameError('Account number must be exactly 12 digits.');
                                } else if (limitedValue.length > 0 && limitedValue.length < 12) {
                                    setAccountNameError(`${12 - limitedValue.length} more digit(s) required.`);
                                } else {
                                    setAccountNameError('');
                                }
                            }}
                            isDisabled={isCreatingSource} />
                    </div>

                    <div className="u-mt">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginBottom: '6px',
                                position: 'relative'
                            }}
                            onMouseLeave={() => setIsBucketInfoVisible(false)}
                        >
                            <span style={{ color: 'var(--eds-text-icon-default)' }}>Bucket Name</span>
                            <span
                                aria-hidden="true"
                                style={{ color: '#d32f2f', fontSize: '14px', lineHeight: 1, fontWeight: 700 }}
                            >
                                *
                            </span>
                            <button
                                type="button"
                                aria-label="Bucket Name format info"
                                onMouseEnter={() => setIsBucketInfoVisible(true)}
                                onFocus={() => setIsBucketInfoVisible(true)}
                                onBlur={() => setIsBucketInfoVisible(false)}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    border: '1px solid #9e9e9e',
                                    background: '#fff',
                                    color: '#616161',
                                    fontSize: '12px',
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    cursor: 'help',
                                    padding: 0
                                }}
                            >
                                i
                            </button>
                            {isBucketInfoVisible && (
                                <div
                                    role="tooltip"
                                    style={{
                                        position: 'absolute',
                                        left: '26px',
                                        top: '-4px',
                                        zIndex: 5,
                                        maxWidth: '300px',
                                        backgroundColor: '#1f2937',
                                        color: '#fff',
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                        padding: '8px 10px',
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                                    }}
                                >
                                    S3 bucket name must be 3-63 characters, lowercase letters, numbers, dots, and hyphens only.
                                </div>
                            )}
                        </div>
                        <EDSTextField
                            name="bucket-name"
                            isInvalid={Boolean(bucketNameError)}
                            label={{ width: '', position: 'top', text: '' }}
                            aria-label="Bucket Name"
                            placeholder="Enter bucket name"
                            isRequired={true}
                            validationError={bucketNameError}
                            width="100%"
                            type="text"
                            value={bucketName}
                            onEDSTextFieldChange={(e) => {
                                const value = e.target.value;
                                setBucketName(value);
                                setBucketNameError(validateBucketNameLive(value));
                            }}
                            isDisabled={isCreatingSource} />
                    </div>

                    <div className="u-mt">
                        <EDSTextField
                            name="prefix"
                            isInvalid={Boolean(prefixError)}
                            label={{ width: '', position: 'top', text: 'Prefix' }}
                            placeholder='Enter prefix (optional, defaults to slash("/"))'
                            isRequired={false}
                            validationError={prefixError}
                            width="100%"
                            type="text"
                            value={prefix}
                            onEDSTextFieldChange={(e) => { setPrefix(e.target.value); if (prefixError) setPrefixError(''); }}
                            isDisabled={isCreatingSource} />
                    </div>

                    <div className="u-mt">
                        <EDSTextField
                            name="display-name"
                            label={{ width: '', position: 'top', text: 'Display Name' }}
                            placeholder="Select or enter display name (optional)"
                            isRequired={false}
                            width="100%"
                            type="text"
                            value={existingDatasetName}
                            onEDSTextFieldChange={(e) => setExistingDatasetName(e.target.value)}
                            isDisabled={isCreatingSource} />
                    </div>

                    <PrefilledField name="contract" label="Contract" value={prefilledData.contract} />
                    <PrefilledField name="business-unit" label="Business Unit" value={prefilledData.businessUnit} />
                    <PrefilledField name="project" label="Project" value={prefilledData.project} />
                    <PrefilledField name="data-partner" label="Data Partner" value={prefilledData.dataPartner} />
                </div>

                <SourceListTable
                    selectedContractId={selectedContractId}
                    onOpenSourceDetails={handleOpenSourceDetails}
                    onDeleteAccount={handleDeleteAccount}
                    onTestConnection={handleTestConnection}
                    onPreviousPage={() => loadAccountsPage(accountsPagination.page - 1)}
                    onNextPage={() => loadAccountsPage(accountsPagination.page + 1)} />
            </div>

        </div>

            <DeleteSourceModal
                isOpen={isDeleteConfirmOpen}
                sourceToDelete={sourceToDelete}
                isDeletingSource={isDeletingSource}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDeleteAccount} />
            <TestConnectionModal
                modalState={testConnectionModal}
                isTestConnectionRunning={isTestConnectionRunning}
                onRunAgain={handleRunTestConnectionAgain}
                onClose={closeTestConnectionModal} /><div style={{ display: 'flex', flexWrap: 'nowrap', gap: '20px', padding: '24px 24px', minHeight: '48px', borderTop: '1px solid #e0e0e0', backgroundColor: 'var(--eds-card-fill-color)', alignItems: 'center', position: 'sticky', bottom: 0, zIndex: 2 }}>
                <EDSButton
                    variant="primary" type="button" label={isCreatingSource ? 'Submitting...' : 'Register'}
                    icon={{ name: 'ico-upload-16', position: 'left' }}
                    onEDSButtonClick={handleSubmit}
                    isDisabled={isCreatingSource}
                    data-testid="btn-Submit" />
                <EDSButton variant="secondary" type="button" label="Back To Contracts" onEDSButtonClick={handleCancel} isDisabled={isCreatingSource} />
            </div></>
    );
};

export default memo(CreateS3Upload);
